import { Alert, Skeleton } from "antd";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useAppSelector } from "src/state/app.hooks";
import { AppDispatch } from "src/state/app.model";
import { getFessDetailsById } from "src/state/feesDetails/fees.action";
import { feesSelector } from "src/state/feesDetails/fees.reducer";
import { getStudentAdmissionDetailsById } from "src/state/student/student.action";
import { studentSelector } from "src/state/student/student.reducer";
import {
  InstallmentStatusType,
  ReceiptLabel,
} from "src/utils/constants/constant";
import ExactReceiptWithComposition from "./ExactReceiptWithComposition";
import ExactReceiptWithGst from "./ExactReceiptWithGst";
import ExactReceiptWithOutGst from "./ExactReceiptWithOutGst";

const StudentReceipt = () => {
  const { invoice_id, admission_id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const feesState = useAppSelector(feesSelector);
  const studentState = useAppSelector(studentSelector);
  const receiptRef = useRef<HTMLDivElement>(null);
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (!admission_id) return;
    dispatch(getFessDetailsById(Number(admission_id)));
    dispatch(getStudentAdmissionDetailsById(Number(admission_id)));
  }, [admission_id, dispatch]);

  const installment = useMemo(
    () =>
      feesState.getById.data.find(
        (installment) =>
          String(installment.invoice?.id || installment.invoice_id) ===
          invoice_id,
      ),
    [feesState.getById.data, invoice_id],
  );
  const invoice = installment?.invoice;
  const student = studentState.getById.data[0];
  const label = student?.package_id === null ? "Course" : "Package";
  const isCancelled = installment?.status === InstallmentStatusType.CANCELLED;

  const renderExactReceipt = () => {
    const props = {
      invoiceData: invoice,
      admissionData: student,
      label,
    };

    if (invoice?.isgst === false) {
      return <ExactReceiptWithOutGst {...props} />;
    }
    if (invoice?.isgst === true && invoice?.iscomposition === true) {
      return <ExactReceiptWithComposition {...props} />;
    }
    return <ExactReceiptWithGst {...props} />;
  };

  const renderReceiptCopy = () => (
    <div className="student-receipt-copy">
      {renderExactReceipt()}
      {isCancelled && (
        <div
          className="student-receipt-cancelled"
        >
          {ReceiptLabel.CANCELLED}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (!invoice || !student || !receiptRef.current) return;

    let cancelled = false;
    let generatedUrl: string | undefined;

    const createPdf = async () => {
      setPdfError(false);
      try {
        await document.fonts?.ready;
        const images = Array.from(
          receiptRef.current?.querySelectorAll("img") ?? [],
        );
        const imageWaitStartedAt = Date.now();
        while (
          images.some((image) => !image.complete || image.naturalWidth === 0) &&
          Date.now() - imageWaitStartedAt < 5000
        ) {
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
        }

        if (cancelled || !receiptRef.current) return;

        const canvas = await html2canvas(receiptRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 5;
        const scale = Math.min(
          (pageWidth - margin * 2) / canvas.width,
          (pageHeight - margin * 2) / canvas.height,
        );
        const width = canvas.width * scale;
        const height = canvas.height * scale;

        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          (pageWidth - width) / 2,
          (pageHeight - height) / 2,
          width,
          height,
          undefined,
          "FAST",
        );

        generatedUrl = URL.createObjectURL(pdf.output("blob"));
        if (!cancelled) setPdfUrl(generatedUrl);
      } catch (error) {
        console.error("Unable to generate student receipt PDF", error);
        if (!cancelled) setPdfError(true);
      }
    };

    createPdf();
    return () => {
      cancelled = true;
      if (generatedUrl) URL.revokeObjectURL(generatedUrl);
    };
  }, [invoice, isCancelled, student]);

  const isLoading = feesState.getById.loading || studentState.getById.loading;

  if (!isLoading && !invoice) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Receipt is not available for this installment."
      />
    );
  }

  return (
    <div>
      <Skeleton
        loading={isLoading || (!!invoice && !pdfUrl && !pdfError)}
        active
      >
        {pdfError ? (
          <Alert
            type="error"
            showIcon
            message="Receipt PDF could not be generated."
          />
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title={`Receipt ${invoice?.invoiceNo || "PDF"}`}
            className={"gx-d-block gx-w-100 receipt-full-height gx-border-0"}
          />
        ) : null}
      </Skeleton>

      {invoice && student && !pdfUrl && !pdfError && (
        <div
          className={"receipt-offscreen-position receipt-offscreen-left receipt-offscreen-top gx-bg-white"}
        >
          <div
            ref={receiptRef}
            className={"payment-receipt receipt-width-page gx-bg-white"}

          >
            {renderReceiptCopy()}
            <hr
              className={"receipt-divider-color receipt-divider-style receipt-divider-spacing"}
            />
            {renderReceiptCopy()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentReceipt;
